import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismCard, PrismIcon } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralSmartInsightsBadgePriority from 'lumen/components/general/smart-insights/badge-priority';
import type {
  SmartInsightDay,
  SmartInsightDayInsightEntry,
} from 'lumen/models';
import type { SmartInsightDayInsightEntrySource } from 'lumen/models/smart-insights/smart-insight-day-insight-entry';

import styles from './list-card-smart-insights.module.css';

const VISIBLE_ENTRY_LIMIT = 3;

interface GeneralSmartInsightsListListCardSmartInsightsSignature {
  Args: {
    selectSmartInsightsDay: (day: SmartInsightDay) => void;
    smartInsightDay: SmartInsightDay;
  };
}

export default class GeneralSmartInsightsListListCardSmartInsights extends Component<GeneralSmartInsightsListListCardSmartInsightsSignature> {
  @service declare smartInsights: Services['smart-insights'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isExpanded = false;

  get formattedStayDate(): string {
    return dayjs(this.args.smartInsightDay.stayDate).format('ddd, MMM D, YYYY');
  }

  get hasHiddenEntries(): boolean {
    return this.hiddenEntryCount > 0;
  }

  get hiddenEntryCount(): number {
    return Math.max(0, this.sortedEntries.length - VISIBLE_ENTRY_LIMIT);
  }

  get showExpandButton(): boolean {
    return this.hasHiddenEntries && !this.isExpanded;
  }

  get sortedEntries(): SmartInsightDayInsightEntry[] {
    return this.args.smartInsightDay.insightEntriesSortedByRelevance;
  }

  get visibleEntries(): SmartInsightDayInsightEntry[] {
    return this.isExpanded
      ? this.sortedEntries
      : this.sortedEntries.slice(0, VISIBLE_ENTRY_LIMIT);
  }

  @action expand(): void {
    this.isExpanded = true;
  }

  @action onClickSource(
    source: SmartInsightDayInsightEntrySource,
    event: MouseEvent,
  ): void {
    event.preventDefault();
    // Navigate to the source page without redirecting to the day detail view.
    // The user is already in context on the list view and should stay there.
    // Right-click → "Open in new tab" bypasses this handler entirely and uses
    // the native <a href>, which includes smartInsightsDay so the new tab
    // opens the detail view — desirable when losing the list view context.
    source.navigateToSource(false, false);

    this.usageAnalytics.track('Smart Insight Source Viewed', {
      eventTrigger: 'smart-insights-list-card',
      product: 'smart-insights',
      ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
        this.args.smartInsightDay,
        this.args.smartInsightDay.stayDate,
      ),
    });
  }

  @action onClickViewDetails(): void {
    this.args.selectSmartInsightsDay(this.args.smartInsightDay);
  }

  <template>
    <div
      class="{{styles.card}} list-card-smart-insights flex flex-col gap-200"
      data-test-id="list-card-smart-insights"
    >
      <div class="flex flex-row items-center justify-between gap-200">
        <h3 data-test-id="list-card-date">{{this.formattedStayDate}}</h3>

        <GeneralSmartInsightsBadgePriority
          @priority={{@smartInsightDay.stayDatePriority}}
        />
      </div>

      <PrismCard @contentLayout="noPadding">
        <:content>
          <div
            class="flex flex-col gap-500 px-400 pt-400 pb-200"
            data-test-id="list-card-content"
          >
            {{#each this.visibleEntries as |entry|}}
              <div class="flex flex-row gap-400" data-test-id="list-card-entry">
                <div class="list-card-smart-insights__icon-container">
                  <PrismIcon
                    @color="on-intelligence"
                    @name={{entry.iconName}}
                    @size="small"
                    data-test-id="list-card-entry-icon"
                  />
                </div>

                <div class="flex flex-col gap-100">
                  <p class="text-300--regular text-neutral-default">
                    {{htmlSafe entry.formattedText}}
                  </p>

                  {{#if entry.source}}
                    <span
                      class="text-200--regular text-neutral-subdued flex flex-row gap-100"
                      data-test-id="list-card-entry-source"
                    >
                      {{t "smart_insights.detail_view.source"}}

                      <a
                        class="prism-link text-neutral-subdued"
                        href={{entry.source.href}}
                        {{on "click" (fn this.onClickSource entry.source)}}
                      >
                        {{entry.source.productName}}
                      </a>
                    </span>
                  {{/if}}
                </div>
              </div>
            {{/each}}

            {{#if this.showExpandButton}}
              <PrismButton
                @buttonType="ghost"
                @icon="chevron-down"
                @isSmall={{true}}
                @label={{t
                  "smart_insights.list_card.show_more"
                  count=this.hiddenEntryCount
                }}
                @onClick={{this.expand}}
                class="self-start"
                data-test-id="list-card-show-more-button"
              />
            {{/if}}

          </div>
        </:content>

        <:footer>
          <PrismButton
            @buttonType="secondary"
            @label={{t "smart_insights.view_details"}}
            @onClick={{this.onClickViewDetails}}
            class="w-full"
            data-test-id="view-details-button"
          />
        </:footer>
      </PrismCard>
    </div>
  </template>
}
